import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'
import { useEffect } from 'react'
import { dehydrate, QueryClient } from '@tanstack/react-query'
import { getEnv } from '@unni/platform'

import { GlobalPageProps } from '../_app'

import { getDefaultMeta, SEOMeta } from '@/shared/utils'
import HospitalListTemplate from '@/components/atomic/templates/HospitalListTemplate'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { postAmplitude, postGA } from '@/core/lib'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { getSearchDistrictCodes, Locales } from '@/utils/language'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import {
  DistrictResponseController,
  DistrictsResponse,
  getAllDistricts,
} from '@/features/district-filter/api/get-districts'
import { HospitalService } from '@/features/hospital/api/hospital.service'
import { createSolarApiClient } from '@/shared/libs/api-client'
import axios, { type AxiosRequestHeaders } from 'axios'
import { prefetchInfiniteSearchHospitals, useInfiniteSearchHospitals } from '@/features/hospital/api/search-hospitals'
import { SearchHospitalsParams, SearchHospitalsSort } from '@/features/hospital/types/hospital.type'
import { useLocales } from '@/shared/hooks/useLocales'

interface HospitalListPageProps extends GlobalPageProps {
  hospitalSearchParams: SearchHospitalsParams
  headers: Record<string, string>
}
const HospitalListPage: NextPage<HospitalListPageProps> = ({ user, hospitalSearchParams }) => {
  const { locale } = useLocales()
  const { data: infiniteData, fetchNextPage, hasNextPage } = useInfiniteSearchHospitals(hospitalSearchParams, locale)
  const hospitals = infiniteData?.pages.flatMap((page) => page.data) || []
  const totalHospitalCount = infiniteData?.pages[0].recordsTotal || 0

  const onClickMore = () => {
    fetchNextPage()
    postAmplitude('click_more_hospital_in_hospital_list', {
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer,
    })
  }

  useEffect(() => {
    postAmplitude('view_hospital_list', {
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer,
    })
  }, [])

  useEffect(() => {
    if (user && user.id !== 0) {
      sessionStorage.setItem('user_id', user.id.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [user])

  useEffect(() => {
    postGA('view_hospital_list', 'pageview')
  }, [])

  useEffect(() => {
    sendAirbridgeEvent(AirbridgeEvents.HOSPITAL_VIEW, {})
  }, [])

  return (
    <ResponsiveLayout>
      <TopDownloadScreen userProfileImage={user?.profileImage} useGnb={true} selectedGnbTabKey="hospital">
        <HospitalListTemplate
          userId={user?.id || 0}
          hospitals={hospitals}
          totalHospitalCount={totalHospitalCount}
          hasMoreHospitals={hasNextPage}
          loadMoreHospitals={async () => {
            await fetchNextPage()
          }}
          onClickMore={onClickMore}
        />
      </TopDownloadScreen>
    </ResponsiveLayout>
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<HospitalListPageProps>> => {
  const contextEnhancer = new ContextEnhancer(ctx)
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
  const q = ctx.query.q ? (ctx.query.q as string) : ''
  const districtCode = ctx.query.district ? (ctx.query.district as string) : ''
  try {
    const [districtResponse] = await Promise.allSettled([getAllDistricts(enhancedContext.apiHeaders)])
    const districtResponseData: DistrictsResponse | undefined =
      districtResponse.status === 'fulfilled' ? districtResponse.value : undefined
    const districtResponseConverter: DistrictResponseController = new DistrictResponseController(districtResponseData)
    const selectedDistrictName: string | undefined = districtResponseConverter.getByCode(districtCode)?.name

    const selectedDistrictCodes: string = districtResponseConverter
      .getTargetDistrictsByCode(districtCode)
      ?.map((district) => district.code)
      ?.join(',')
    const defaultDistrictCodes = getSearchDistrictCodes(enhancedContext.locale, { isCrossCountry: false }).join(',')
    const districtCodeList = selectedDistrictCodes || defaultDistrictCodes

    const hospitalService = new HospitalService(
      createSolarApiClient({ headers: enhancedContext.apiHeaders as unknown as AxiosRequestHeaders }),
    )

    const hospitalSearchParams: SearchHospitalsParams = {
      q,
      sort: SearchHospitalsSort.RCMD,
      districtCodeList,
      start: 0,
      length: 20,
    }

    const queryClient = new QueryClient()
    const { queryData } = await prefetchInfiniteSearchHospitals(
      queryClient,
      hospitalService,
      hospitalSearchParams,
      enhancedContext.locale,
    )

    const totalHospitalCount = queryData?.pages[0].recordsTotal

    const queryString = new URLSearchParams({
      ...(q && { q }),
      ...(districtCode && { district: districtCode }),
    })

    /**
     * SEO robot 수집 정책
     * : 검색 결과가 4개 미만일 경우 저평가 페이지로 규정하여 검색 결과를 노출하지 않음.
     */
    if (!totalHospitalCount || totalHospitalCount < 4) {
      ctx.res.setHeader('x-robots-tag', 'noindex, follow')
    }

    return {
      props: {
        user: enhancedContext.user,
        hospitalSearchParams,
        headers: {
          Authorization: enhancedContext.token,
          'Accept-Language': enhancedContext.locale,
          'x-forwarded-for': enhancedContext.userIp,
        },
        meta: getMeta(
          enhancedContext.locale,
          {
            keyword: q,
            totalLength: totalHospitalCount || 0,
            districtName: selectedDistrictName,
          },
          {
            canonical: `${getEnv('NEXT_PUBLIC_HOST')}${enhancedContext.pathnamePrefix}/hospitals${queryString ? `?${queryString}` : ''}`,
          },
        ),
        dehydratedState: dehydrate(queryClient),
      },
    }
  } catch (e) {
    console.error(e)

    return {
      notFound: true,
    }
  }
}

const getMeta = (
  locale: Locales,
  data: {
    keyword: string
    totalLength: number
    districtName?: string
    treatment?: string
  },
  override?: {
    canonical?: string
  },
): SEOMeta => {
  const commonMeta = getDefaultMeta(locale)
  const year = new Date(Date.now()).getFullYear()
  if (locale === Locales.JA_JP) {
    const keywordText = data.keyword ? `「${data.keyword}」 ` : ''
    const districtText = data.districtName ? `${data.districtName} ` : ''
    const prefixText = `${districtText ? `${districtText}エリア ` : ''}${keywordText ? `${keywordText} ` : ''}`.trim()
    const jpTitle = `${prefixText}クリニックおすすめ・比較 (${year}) | カンナムオンニ`
    const jpDescription = `${prefixText}クリニック情報及びおすすめリスト。 ${data.totalLength}件のクリニック施術・整形の口コミと値段をチェック。`

    return {
      ...commonMeta,
      title: jpTitle,
      description: jpDescription,
      ...override,
    }
  }

  if (locale === Locales.EN_US) {
    const prefixText = `${data.districtName ? `${data.districtName} ` : ''}${data.keyword ? `${data.keyword} ` : ''}`
    const enTitle = `${prefixText}Recommended clinic・price comparison (${year}) | Unni`
    const enDescription = `${prefixText} Check the price of ${data.keyword} etc. ${data.totalLength} reviews are available.`

    return {
      ...commonMeta,
      title: enTitle,
      description: enDescription,
      ...override,
    }
  }

  if (locale === Locales.TH_TH) {
    const keywordText = data.keyword || data.districtName || ''
    const thTitle = `ดูโปรโมชั่น และข้อมูล ${keywordText} | UNNI - แอปเกาหลีอันดับ 1`
    const thDescription = `โปรโมชั่นดีๆ รีวิวจริงของ ${keywordText} และอีก ${data.totalLength} คลินิก ให้คุณเลือกได้อย่างมั่นใจ`

    return {
      ...commonMeta,
      title: thTitle,
      description: thDescription,
      ...override,
    }
  }

  const keywordText = data.keyword ? `'${data.keyword}' ` : ''
  const districtText = data.districtName ? `${data.districtName} ` : ''
  const prefixText = `${districtText ? `${districtText}지역 ` : ''}${keywordText ? `${keywordText} ` : ''}`
  const krTitle = `${prefixText}병원추천・비교 (${year}) | 강남언니`
  const krDescription = `${prefixText}병원 정보 및 추천 리스트. ${data.totalLength}개 병원 시술・성형의 후기와 가격을 확인해보세요.`

  return {
    ...commonMeta,
    title: krTitle,
    description: krDescription,
    ...override,
  }
}

export default HospitalListPage
